import React, { useState } from 'react';

import { formatMoney } from '../../../commodity/format/money';
import { CourseDetailResponse, OfferMeetings } from '../api/courses';
import { courseZone, familyZoneLabel, formatDayAndTime } from '../time/zonedTime';

/** How many meetings the page shows before Show All Meetings. */
const FIRST_MEETINGS = 3;

const box: React.CSSProperties = {
  border: '1px solid #d1d5da',
  borderRadius: 8,
  padding: '12px 14px',
  marginBottom: 12,
  fontSize: 14,
  color: '#444',
};

const seatsText = (seatsLeft: number | null): string | null => {
  if (seatsLeft === null) return 'Cupo no disponible por ahora';
  if (seatsLeft === 0) return 'Sin cupo';
  return seatsLeft === 1 ? 'Queda 1 lugar' : `Quedan ${seatsLeft} lugares`;
};

const Summary: React.FC<{ meetings: OfferMeetings; course: CourseDetailResponse }> = ({
  meetings,
  course,
}) => (
  <p style={{ margin: '0 0 8px' }}>
    {meetings.meetingCount === 1 ? '1 clase' : `${meetings.meetingCount} clases`}
    {meetings.meetingsPerWeek ? ` · ${meetings.meetingsPerWeek} por semana` : ''}
    {` · ${course.duration} min`}
    {` · ${seatsText(meetings.seatsLeft)}`}
    {` · ${formatMoney(course.price, course.currency)} por sesión`}
  </p>
);

/**
 * DEC-S2-S04-04/05 — Classes and Show All Meetings.
 *
 * Everything here comes from the offer's materialized occurrences; there is no
 * list of its own and no prose written from the timetable. Three states that
 * must not read alike: the meetings could not be read, there are none, and an
 * appointment-based course that has none by design. The page's own loading
 * state covers the fourth: this is drawn only once the course has arrived.
 */
const CourseMeetings: React.FC<{ course: CourseDetailResponse }> = ({ course }) => {
  const [showAll, setShowAll] = useState(false);
  const meetings = course.meetings;

  if (meetings === null || meetings === undefined) {
    return (
      <section aria-label="Clases" style={box}>
        <strong style={{ display: 'block', marginBottom: 4 }}>Clases</strong>
        <span role="alert">No pudimos cargar las fechas de este curso.</span>
      </section>
    );
  }

  if (meetings.occurrences.length === 0) {
    return (
      <section aria-label="Clases" style={box}>
        <strong style={{ display: 'block', marginBottom: 4 }}>Clases</strong>
        {course.scheduleType === 'RECURRING'
          ? 'Este curso no tiene fechas próximas.'
          : 'Se agenda por cita según la disponibilidad del profesor.'}
      </section>
    );
  }

  const tz = courseZone(meetings.occurrences[0].timezone);
  const shown = showAll ? meetings.occurrences : meetings.occurrences.slice(0, FIRST_MEETINGS);

  return (
    <section aria-label="Clases" style={box}>
      <strong style={{ display: 'block', marginBottom: 4 }}>Clases</strong>
      <Summary meetings={meetings} course={course} />
      <ul style={{ margin: '0 0 8px', paddingLeft: 18 }}>
        {shown.map((o) => (
          <li key={o.occurrenceId}>
            {formatDayAndTime(o.startsAt, tz)} · {o.durationMinutes} min
          </li>
        ))}
      </ul>
      <p style={{ margin: 0, fontSize: 12, color: '#666' }}>{familyZoneLabel(tz)}</p>
      {meetings.occurrences.length > FIRST_MEETINGS && (
        <button
          type="button"
          onClick={() => setShowAll((open) => !open)}
          aria-expanded={showAll}
          style={{
            marginTop: 8,
            background: 'none',
            border: 'none',
            padding: 0,
            color: '#1971c2',
            cursor: 'pointer',
            fontSize: 14,
          }}
        >
          {showAll ? 'Ver menos clases' : 'Ver todas las clases'}
        </button>
      )}
    </section>
  );
};

export default CourseMeetings;
